export type LegalPolicySection = {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
  afterBullets?: string[];
};

export type LegalPolicy = {
  title: string;
  effectiveDate: string;
  intro: string[];
  sections: LegalPolicySection[];
};

export type LegalPolicyKey =
  | "terms"
  | "privacy"
  | "refundPolicy"
  | "listingGuidelines";

// This is the approved document revision token, not a timezone-derived date.
// That revision becomes effective at the separately approved 1 June 2026 instant.
export const LEGAL_VERSION = "2026-05-31";
export const LEGAL_EFFECTIVE_AT_MS = Date.parse(
  "2026-06-01T00:00:00+03:00"
);

export const legalPolicies: Record<LegalPolicyKey, LegalPolicy> = {
  terms: {
    title: "Terms of Use",
    effectiveDate: "1 June 2026",
    intro: [
      "Welcome to Soko Grid. These Terms of Use explain the rules for using the Soko Grid website, app, business directory, and related services.",
      "By accessing or using Soko Grid, creating an account, submitting a business listing, or paying for a subscription, you agree to these Terms of Use.",
    ],
    sections: [
      {
        heading: "1. About Soko Grid",
        paragraphs: [
          "Soko Grid is a Kenyan business directory and online storefront platform that helps product and service providers create searchable business profiles. Customers can view business information such as products, services, location, contact details, website links, social media links, and WhatsApp contact options.",
          "Soko Grid is owned and operated by Soko Grid Ltd.",
        ],
      },
      {
        heading: "2. Who May Use Soko Grid",
        paragraphs: [
          "You may use Soko Grid if you are legally able to use online services and provide accurate information. Business owners are responsible for ensuring that they have authority to create and manage the business listing they submit.",
        ],
      },
      {
        heading: "3. Business Owner Accounts",
        paragraphs: [
          "Business owners may create an account to submit and manage business listings. You are responsible for:",
        ],
        bullets: [
          "Keeping your login details secure.",
          "Providing accurate business information.",
          "Updating your listing when details change.",
          "Ensuring that uploaded images, descriptions, prices, contacts, and links are lawful and accurate.",
          "Ensuring that phone numbers, WhatsApp numbers, website links, social links, and location pins belong to or are authorized by the business.",
        ],
        afterBullets: [
          "Soko Grid may suspend or restrict access if an account is used in a misleading, harmful, fraudulent, or unauthorized way.",
        ],
      },
      {
        heading: "4. Business Listings and Approval",
        paragraphs: [
          "Submitting a business listing does not guarantee publication. Listings may go through admin review before they become visible to the public.",
          "A business listing may be:",
        ],
        bullets: [
          "Draft",
          "Pending review",
          "Approved",
          "Rejected",
          "Returned for changes",
          "Deactivated or removed where necessary",
        ],
        afterBullets: [
          "Soko Grid may reject, request changes, deactivate, or remove a listing if it appears inaccurate, incomplete, misleading, illegal, harmful, abusive, spam-like, or against these Terms.",
        ],
      },
      {
        heading: "5. Public Business Information",
        paragraphs: [
          "When a business listing is approved and published, some information may become visible to the public, including:",
        ],
        bullets: [
          "Business name",
          "Category and subcategory",
          "County, city, area, and location details",
          "Business phone number",
          "WhatsApp contact or catalog link",
          "Website and social media links",
          "Business description",
          "Products or services",
          "Images uploaded by the owner",
          "Opening hours and other listing details",
        ],
        afterBullets: [
          "Business owners should not submit private information that they do not want displayed publicly.",
        ],
      },
      {
        heading: "6. Customer Interactions",
        paragraphs: [
          "Soko Grid helps customers discover and contact businesses. However, Soko Grid is not the seller of the products or services listed by business owners.",
          "Any purchase, delivery, booking, payment, refund, warranty, appointment, or dispute between a customer and a listed business is between the customer and that business, unless Soko Grid expressly states otherwise.",
          "Soko Grid does not guarantee:",
        ],
        bullets: [
          "That customers will contact a business.",
          "That a business will receive sales or leads.",
          "That business information is always complete or error-free.",
          "That a listed business will provide a product or service as advertised.",
          "That a customer and business will complete a transaction successfully.",
        ],
      },
      {
        heading: "7. Prohibited Content and Activities",
        paragraphs: [
          "You must not use Soko Grid to submit, promote, or share content that is:",
        ],
        bullets: [
          "Illegal or fraudulent.",
          "Misleading or deceptive.",
          "Abusive, hateful, threatening, or harassing.",
          "Sexually exploitative or harmful.",
          "Infringing on another person's intellectual property rights.",
          "Using stolen images, fake business names, or unauthorized contact details.",
          "Promoting scams, impersonation, or false claims.",
          "Interfering with the security or operation of Soko Grid.",
        ],
        afterBullets: [
          "Soko Grid may remove such content and may suspend or terminate accounts involved in prohibited activity.",
        ],
      },
      {
        heading: "8. Images, Logos, and Business Content",
        paragraphs: [
          "By uploading images, logos, descriptions, product details, service details, or other content to Soko Grid, you confirm that:",
        ],
        bullets: [
          "You own the content or have permission to use it.",
          "The content does not violate another person's rights.",
          "Soko Grid may display the content as part of your business listing.",
          "Soko Grid may resize, compress, or format uploaded images for performance and display.",
        ],
        afterBullets: ["You remain responsible for the content you upload."],
      },
      {
        heading: "9. Categories, Locations, and Suggestions",
        paragraphs: [
          "Business owners may suggest missing categories, subcategories, cities, areas, or other listing details. Soko Grid may review, approve, reject, edit, merge, or remove suggestions to keep the directory accurate and organized.",
          "Official categories, subcategories, counties, cities, and areas may be controlled by Soko Grid admins.",
        ],
      },
      {
        heading: "10. Subscriptions and Payments",
        paragraphs: [
          "Soko Grid may offer paid business listing subscriptions. Subscription fees, duration, and benefits will be shown before payment.",
          "Payments may be processed through M-Pesa or other supported payment methods. By paying, you confirm that you have reviewed the price, subscription period, and applicable policies before completing payment.",
          "A paid subscription does not guarantee approval if the submitted business listing violates Soko Grid rules or cannot be verified. Refunds are handled according to the Soko Grid Refund Policy.",
        ],
      },
      {
        heading: "11. Changes to Services and Pricing",
        paragraphs: [
          "Soko Grid may update, improve, suspend, or discontinue parts of the platform. Soko Grid may also update pricing, features, subscription plans, or listing rules. Where practical, important changes will be communicated through the website, app, or contact details provided by users.",
        ],
      },
      {
        heading: "12. Intellectual Property",
        paragraphs: [
          "The Soko Grid name, branding, design, platform features, software, and related materials belong to Soko Grid Ltd or its licensors. You may not copy, reproduce, sell, or misuse Soko Grid's branding, design, or platform without written permission.",
        ],
      },
      {
        heading: "13. Limitation of Liability",
        paragraphs: [
          'Soko Grid aims to provide a useful and reliable service, but the platform is provided on an "as available" basis. To the maximum extent allowed by law, Soko Grid Ltd will not be responsible for indirect losses, lost profits, lost business opportunities, customer disputes, incorrect business information submitted by owners, third-party links, downtime, or actions of listed businesses or customers.',
        ],
      },
      {
        heading: "14. Account Suspension or Termination",
        paragraphs: [
          "Soko Grid may suspend, restrict, deactivate, or terminate an account or listing if:",
        ],
        bullets: [
          "The user violates these Terms.",
          "The listing contains false or harmful information.",
          "Required payment is not completed or is reversed.",
          "The business appears fraudulent, inactive, unsafe, or unauthorized.",
          "The platform is being misused.",
        ],
      },
      {
        heading: "15. Reporting Issues",
        paragraphs: [
          "Users may report inaccurate listings, suspicious businesses, harmful content, or technical issues through the contact or report options provided by Soko Grid.",
        ],
      },
      {
        heading: "16. Changes to These Terms",
        paragraphs: [
          "Soko Grid may update these Terms from time to time. The updated version will be posted on the website or app with a new effective date. Continued use of Soko Grid after updates means you accept the updated Terms.",
        ],
      },
      {
        heading: "17. Contact Us",
        paragraphs: [
          "For questions about these Terms, contact:",
          "Soko Grid Ltd",
          "Email: sokogrid@gmail.com",
          "Website: sokogrid.com",
        ],
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    effectiveDate: "1 June 2026",
    intro: [
      "This Privacy Policy explains how Soko Grid collects, uses, stores, and protects personal information when you use our website, app, business directory, and related services.",
      "By using Soko Grid, creating an account, submitting a business listing, contacting a business, or communicating with us, you agree to this Privacy Policy.",
    ],
    sections: [
      {
        heading: "1. About Soko Grid",
        paragraphs: [
          "Soko Grid is a Kenyan business directory and online storefront platform. We help business owners create searchable business profiles that customers can view and use to contact businesses.",
          "Soko Grid is owned and operated by Soko Grid Ltd.",
        ],
      },
      {
        heading: "2. Information We Collect",
        paragraphs: [
          "We may collect different types of information depending on how you use Soko Grid.",
        ],
      },
      {
        heading: "2.1 Account Information",
        paragraphs: [
          "When a business owner creates an account, we may collect:",
        ],
        bullets: [
          "Name",
          "Email address",
          "Phone number",
          "Login/authentication details",
          "Account status and role",
        ],
      },
      {
        heading: "2.2 Business Listing Information",
        paragraphs: [
          "When a business owner creates or updates a listing, we may collect:",
        ],
        bullets: [
          "Business name",
          "Business category and subcategory",
          "Business description",
          "Products and services",
          "Business phone number",
          "WhatsApp number or WhatsApp catalog link",
          "Website and social media links",
          "County, city, area, address, and map/location details",
          "Opening hours",
          "Business logo, profile image, product photos, or service photos",
          "Listing status and admin review information",
        ],
      },
      {
        heading: "2.3 Payment Information",
        paragraphs: [
          "When subscriptions are introduced, we may collect payment-related information such as:",
        ],
        bullets: [
          "Payment phone number",
          "M-Pesa transaction reference",
          "Payment status",
          "Subscription plan",
          "Subscription start and expiry dates",
          "Payment confirmation details",
        ],
        afterBullets: [
          "Soko Grid does not intend to store sensitive payment credentials such as PINs.",
        ],
      },
      {
        heading: "2.4 Customer and Visitor Information",
        paragraphs: [
          "When customers or visitors use Soko Grid, we may collect:",
        ],
        bullets: [
          "Search activity",
          "Saved businesses on the device or browser",
          "Contact/report messages submitted to Soko Grid",
          "Technical information such as device, browser, or usage data where necessary for security, analytics, and improvement",
        ],
      },
      {
        heading: "2.5 Reports and Support Requests",
        paragraphs: [
          "If you report a business or contact Soko Grid for help, we may collect:",
        ],
        bullets: [
          "Your name or contact details if provided",
          "Details of the issue",
          "Page or business being reported",
          "Screenshots or supporting information if submitted",
        ],
      },
      {
        heading: "3. How We Use Information",
        paragraphs: ["We use information to:"],
        bullets: [
          "Create and manage user accounts.",
          "Allow business owners to create and update listings.",
          "Review, approve, reject, publish, or deactivate listings.",
          "Display approved business listings to customers.",
          "Help customers find and contact businesses.",
          "Process subscriptions and payment confirmations.",
          "Respond to support requests and reports.",
          "Improve search, directory features, safety, and user experience.",
          "Prevent fraud, abuse, spam, unauthorized access, and misuse.",
          "Comply with legal, regulatory, accounting, or security requirements.",
        ],
      },
      {
        heading: "4. Public Business Information",
        paragraphs: [
          "Business owners should understand that approved and published business listings are public. This means customers may see and use public listing information such as:",
        ],
        bullets: [
          "Business name",
          "Business phone number",
          "WhatsApp contact option",
          "Location details",
          "Business description",
          "Product or service details",
          "Images",
          "Website and social links",
          "Opening hours",
        ],
        afterBullets: [
          "Business owners should only submit information that they are authorized to share publicly.",
        ],
      },
      {
        heading: "5. How We Share Information",
        paragraphs: [
          "We do not sell personal information.",
          "We may share or make information available in these situations:",
        ],
        bullets: [
          "Public business listing information is displayed to users of Soko Grid.",
          "Service providers may process data for hosting, authentication, storage, analytics, payments, communication, or security.",
          "Payment providers may process payment-related information.",
          "We may disclose information where required by law, regulation, legal process, or lawful authority.",
          "We may share information to protect Soko Grid, users, businesses, customers, or the public from fraud, abuse, or harm.",
        ],
      },
      {
        heading: "6. Third-Party Services",
        paragraphs: [
          "Soko Grid may use third-party services to operate the platform, including services for:",
        ],
        bullets: [
          "Authentication",
          "Database storage",
          "File/image storage",
          "Hosting",
          "Maps",
          "Payments",
          "Email or support communication",
          "Analytics or security",
        ],
        afterBullets: [
          "These services may process information according to their own policies and legal obligations.",
        ],
      },
      {
        heading: "7. Data Storage and Security",
        paragraphs: [
          "We take reasonable steps to protect information from unauthorized access, misuse, loss, or alteration. These steps may include access controls, authentication, security rules, admin permissions, and secure service providers.",
          "However, no online system is completely secure. Users should also protect their login details and avoid sharing sensitive information unnecessarily.",
        ],
      },
      {
        heading: "8. Data Retention",
        paragraphs: [
          "We keep information for as long as necessary to provide Soko Grid services, manage accounts, maintain business listings, comply with legal obligations, resolve disputes, prevent fraud, and keep business records.",
          "If a business listing is deleted or deactivated, some information may still be retained where necessary for legal, security, payment, audit, or dispute purposes.",
        ],
      },
      {
        heading: "9. Your Rights",
        paragraphs: [
          "Depending on applicable law, you may have rights over your personal data, including the right to:",
        ],
        bullets: [
          "Be informed about how your data is used.",
          "Request access to your personal data.",
          "Request correction of false or misleading data.",
          "Object to certain processing.",
          "Request deletion of false or misleading data.",
          "Withdraw consent where processing is based on consent.",
        ],
        afterBullets: [
          "To make a privacy request, contact us using the email below.",
        ],
      },
      {
        heading: "10. Owner Updates and Corrections",
        paragraphs: [
          "Business owners can update many business details through their account. Some details may require admin review before becoming public.",
          "Soko Grid may also correct, hide, deactivate, or remove information that appears inaccurate, unsafe, misleading, or against platform rules.",
        ],
      },
      {
        heading: "11. Children's Privacy",
        paragraphs: [
          "Soko Grid is intended for business owners and customers looking for businesses. It is not designed for children to create business listings. If we become aware that a child has provided personal information without appropriate permission, we may remove it.",
        ],
      },
      {
        heading: "12. Cookies, Local Storage, and Similar Technologies",
        paragraphs: [
          "Soko Grid may use browser storage, local storage, cookies, or similar technologies to support features such as saved businesses, login sessions, preferences, security, and performance.",
          "Users may control some of these technologies through browser or device settings, but disabling them may affect platform functionality.",
        ],
      },
      {
        heading: "13. Links to Other Websites",
        paragraphs: [
          "Business listings may include links to websites, social media pages, maps, WhatsApp, or other third-party services. Soko Grid is not responsible for the privacy practices, content, or security of third-party websites or services.",
        ],
      },
      {
        heading: "14. Changes to This Privacy Policy",
        paragraphs: [
          "We may update this Privacy Policy from time to time. The updated version will be posted on the website or app with a new effective date.",
        ],
      },
      {
        heading: "15. Contact Us",
        paragraphs: [
          "For privacy questions or data requests, contact:",
          "Soko Grid Ltd",
          "Email: sokogrid@gmail.com",
          "Website: sokogrid.com",
        ],
      },
    ],
  },
  refundPolicy: {
    title: "Refund Policy",
    effectiveDate: "1 June 2026",
    intro: [
      "This Refund Policy explains when payments made to Soko Grid may or may not be refunded.",
      "By paying for a Soko Grid subscription or service, you agree to this Refund Policy.",
    ],
    sections: [
      {
        heading: "1. About Soko Grid Subscriptions",
        paragraphs: [
          "Soko Grid may offer paid subscriptions that allow business owners to create and maintain business listings on the Soko Grid platform.",
          "Payment may be made through M-Pesa or another supported payment method. Subscription price, duration, and benefits will be shown before payment.",
        ],
      },
      {
        heading: "2. Payment Before Listing Approval",
        paragraphs: [
          "Soko Grid may require payment before a business owner creates or submits a listing. However, payment does not automatically guarantee publication.",
          "Listings may still require admin review. Soko Grid may approve, reject, request changes, deactivate, or remove listings based on platform rules, accuracy, safety, and compliance.",
        ],
      },
      {
        heading: "3. When Refunds May Be Approved",
        paragraphs: [
          "Soko Grid may approve a refund in situations such as:",
        ],
        bullets: [
          "You were charged twice for the same subscription by mistake.",
          "Your payment was successful, but Soko Grid failed to activate the subscription due to a technical issue.",
          "You paid but could not create a business listing because of a confirmed Soko Grid system error.",
          "Your listing was rejected before publication and cannot reasonably be corrected.",
          "Soko Grid cancels your listing before publication for reasons not caused by your actions.",
          "A payment was made in error and no subscription benefit was used.",
        ],
        afterBullets: [
          "Refund approval depends on review of the payment, account, listing status, and circumstances.",
        ],
      },
      {
        heading: "4. When Refunds May Not Be Approved",
        paragraphs: ["Soko Grid may decline a refund where:"],
        bullets: [
          "The business listing has already been approved and published.",
          "The business owner changes their mind after payment.",
          "The business owner deletes or stops using their listing after approval.",
          "The business owner submitted false, misleading, unauthorized, or prohibited information.",
          "The listing was rejected or removed because the owner violated Soko Grid Terms of Use or Listing Guidelines.",
          "The owner expected more calls, WhatsApp messages, views, leads, or sales but did not receive them.",
          "The subscription period has already started and the service has been made available.",
          "The issue is caused by incorrect information provided by the owner, such as wrong phone number, wrong business details, or wrong location.",
        ],
      },
      {
        heading: "5. No Guarantee of Sales or Leads",
        paragraphs: [
          "Soko Grid helps customers discover and contact businesses. However, Soko Grid does not guarantee that a listed business will receive calls, WhatsApp messages, customers, sales, bookings, or revenue.",
          "Lack of leads, sales, or customer inquiries is not by itself a reason for a refund.",
        ],
      },
      {
        heading: "6. Refund Request Period",
        paragraphs: [
          "Refund requests should be submitted as soon as possible after payment or after the issue occurs.",
          "Soko Grid may require refund requests to be submitted within a reasonable period, such as within 7 days of payment, unless a longer period is required by law or approved by Soko Grid.",
        ],
      },
      {
        heading: "7. Information Required for Refund Requests",
        paragraphs: ["To request a refund, you may be asked to provide:"],
        bullets: [
          "Full name",
          "Business name",
          "Email used on Soko Grid",
          "Phone number used for payment",
          "M-Pesa transaction code",
          "Date of payment",
          "Reason for refund request",
          "Any supporting screenshots or details",
        ],
        afterBullets: [
          "Failure to provide enough information may delay or prevent processing of the refund request.",
        ],
      },
      {
        heading: "8. Refund Review and Processing",
        paragraphs: [
          "Soko Grid will review refund requests based on payment records, listing status, account activity, and platform rules.",
          "If approved, refunds will normally be returned through the original payment method or another reasonable method supported by Soko Grid.",
          "Processing time may depend on payment provider timelines, verification requirements, and availability of accurate payment details.",
        ],
      },
      {
        heading: "9. Partial Refunds",
        paragraphs: [
          "Soko Grid may offer a partial refund where appropriate, especially where part of the subscription period or service has already been used.",
        ],
      },
      {
        heading: "10. Fraud, Abuse, and Misuse",
        paragraphs: [
          "Soko Grid may refuse refunds where there is suspected fraud, abuse, false claims, repeated misuse, unauthorized payment activity, or violation of Soko Grid policies.",
          "Soko Grid may also suspend or terminate accounts involved in fraudulent or abusive refund requests.",
        ],
      },
      {
        heading: "11. Changes to This Refund Policy",
        paragraphs: [
          "Soko Grid may update this Refund Policy from time to time. The updated version will be posted on the website or app with a new effective date.",
        ],
      },
      {
        heading: "12. Contact Us",
        paragraphs: [
          "To request a refund or ask a payment question, contact:",
          "Soko Grid Ltd",
          "Email: sokogrid@gmail.com",
          "Website: sokogrid.com",
        ],
      },
    ],
  },
  listingGuidelines: {
    title: "Listing Guidelines",
    effectiveDate: "1 June 2026",
    intro: [
      "These Listing Guidelines explain what business owners may submit to Soko Grid and how listings are reviewed.",
      "Soko Grid is a business directory and online storefront platform that helps customers find and contact businesses in Kenya.",
    ],
    sections: [
      {
        heading: "1. Accurate Business Information",
        paragraphs: [
          "Business owners must provide accurate and up-to-date information, including:",
        ],
        bullets: [
          "Business name",
          "Category and subcategory",
          "County, city, area, and location details",
          "Phone number and WhatsApp contact",
          "Website and social media links",
          "Products or services offered",
          "Opening hours",
          "Business description",
          "Images, logo, or shop/profile photo",
        ],
        afterBullets: [
          "You should only submit information that belongs to your business or that you are authorized to use.",
        ],
      },
      {
        heading: "2. Admin Review",
        paragraphs: [
          "Submitting a listing does not mean it will appear publicly immediately.",
          "Soko Grid may review listings before publication. A listing may be approved, rejected, returned for changes, deactivated, or removed if necessary.",
          "Soko Grid may request changes if information is missing, unclear, inaccurate, misleading, or against these Guidelines.",
        ],
      },
      {
        heading: "3. Allowed Listings",
        paragraphs: [
          "Soko Grid is intended for genuine product and service providers, including shops, professionals, service businesses, suppliers, and local businesses that want customers to find and contact them.",
          "Listings should clearly describe what the business offers.",
        ],
      },
      {
        heading: "4. Not Allowed",
        paragraphs: [
          "Soko Grid may reject or remove listings that contain:",
        ],
        bullets: [
          "False or misleading business information",
          "Fake business names or impersonation",
          "Unauthorized phone numbers, WhatsApp numbers, images, or links",
          "Illegal products or services",
          "Scam, fraud, pyramid, or deceptive offers",
          "Abusive, hateful, threatening, or harmful content",
          "Adult, exploitative, or unsafe content",
          "Stolen images, logos, or copyrighted material used without permission",
          "Spam, duplicate listings, or keyword stuffing",
          "Wrong categories or locations intended to manipulate search results",
        ],
      },
      {
        heading: "5. Images and Photos",
        paragraphs: [
          "Uploaded images should be clear, relevant, and connected to the business.",
          "Do not upload images that are stolen, misleading, offensive, low quality, unrelated to the business, or used without permission.",
          "Soko Grid may resize, crop, compress, or reject images to improve platform quality and performance.",
        ],
      },
      {
        heading: "6. Categories and Locations",
        paragraphs: [
          "Business owners should choose the most accurate category, subcategory, county, city, and area.",
          "If a category, subcategory, city, or area is missing, owners may suggest it. Soko Grid may approve, reject, edit, or merge suggestions to keep the directory organized.",
        ],
      },
      {
        heading: "7. Business Contact Details",
        paragraphs: [
          "Phone numbers, WhatsApp numbers, website links, map pins, and social media links must be correct and authorized by the business.",
          "Soko Grid may reject or hide contact details that appear incorrect, unsafe, misleading, or unauthorized.",
        ],
      },
      {
        heading: "8. Changes After Approval",
        paragraphs: [
          "Approved businesses may still be reviewed if they are edited, reported, inactive, misleading, or found to violate these Guidelines.",
          "Soko Grid may request corrections, unpublish, deactivate, or remove listings where necessary.",
        ],
      },
      {
        heading: "9. No Guarantee of Sales",
        paragraphs: [
          "Soko Grid helps customers discover and contact businesses, but it does not guarantee calls, WhatsApp messages, views, leads, sales, bookings, or revenue.",
        ],
      },
      {
        heading: "10. Reporting a Listing",
        paragraphs: [
          "Users may report inaccurate, suspicious, harmful, or misleading listings through the report options provided on Soko Grid.",
        ],
      },
      {
        heading: "11. Contact Us",
        paragraphs: [
          "For questions about these Listing Guidelines, contact:",
          "Soko Grid Ltd",
          "Email: sokogrid@gmail.com",
          "Website: sokogrid.com",
        ],
      },
    ],
  },
};
